import type { LocationQuery, LocationQueryValue } from 'vue-router'
import { endOfMonth, startOfMonth } from 'date-fns'
import type { LensPeriod } from '@/Network/Types/Requests/DashboardSavedViewRequests'
import type { MatrixMetric } from '@/Network/Types/Responses/StationResponses'
import { getPeriodRange } from '../lenses/monthCalendar'

/** Recorte da Matriz (spec §4.1): mês inteiro ou uma semana seg–dom. */
export type MatrixCut = 'mes' | 'semana'
/** Métrica da Matriz como aparece na URL (`metrica`). */
export type MatrixMetricUi = 'plantoes' | 'horas'
/** Métrica como o BE espera em `GET /stations/matrix-summary?metric=`. */
export type MatrixApiMetric = MatrixMetric

export type MatrixQueryState = { cut: MatrixCut; metric: MatrixMetricUi }

export const DEFAULT_MATRIX_STATE: Readonly<MatrixQueryState> = Object.freeze({
  cut: 'mes',
  metric: 'plantoes'
})

const CUT_VALUES: readonly MatrixCut[] = ['mes', 'semana']
const METRIC_VALUES: readonly MatrixMetricUi[] = ['plantoes', 'horas']

/** Chaves da URL que são estado da Matriz (nunca filtro da API de `GET /stations`). */
export const MATRIX_QUERY_KEYS: readonly string[] = ['recorte', 'metrica']

const firstValue = (value: LocationQueryValue | LocationQueryValue[] | undefined): string | null =>
  Array.isArray(value) ? (value[0] ?? null) : (value ?? null)

export function parseMatrixQuery(query: LocationQuery): MatrixQueryState {
  const cut = firstValue(query.recorte)
  const metric = firstValue(query.metrica)
  return {
    cut: CUT_VALUES.includes(cut as MatrixCut) ? (cut as MatrixCut) : DEFAULT_MATRIX_STATE.cut,
    metric: METRIC_VALUES.includes(metric as MatrixMetricUi)
      ? (metric as MatrixMetricUi)
      : DEFAULT_MATRIX_STATE.metric
  }
}

/** Só grava o que difere do padrão (URL limpa com `recorte=mes`/`metrica=plantoes`). */
export function serializeMatrixQuery(state: MatrixQueryState): Record<string, string> {
  const out: Record<string, string> = {}
  if (state.cut !== DEFAULT_MATRIX_STATE.cut) out.recorte = state.cut
  if (state.metric !== DEFAULT_MATRIX_STATE.metric) out.metrica = state.metric
  return out
}

export function stripMatrixKeys<T extends Record<string, unknown>>(query: T): T {
  const copy: Record<string, unknown> = { ...query }
  MATRIX_QUERY_KEYS.forEach((key) => delete copy[key])
  return copy as T
}

/** Flags que decidem se um período da lente está disponível (Matriz, Mapa, Timeline). */
export type PeriodFlags = {
  enabledDashboardMatrix: boolean
  enabledDashboardMap: boolean
  enabledDashboardTimeline: boolean
}

/**
 * Flag OFF: `periodo=matriz` (Onda 2.1) ou `periodo=mapa` (Onda 2.4) — URL ou visão salva —
 * cai para o Mês; `periodo=timeline` (Onda 2.3) cai para a Semana.
 */
export function resolvePeriodForFlag(period: LensPeriod, flags: PeriodFlags): LensPeriod {
  if (period === 'matriz' && !flags.enabledDashboardMatrix) return 'mes'
  if (period === 'mapa' && !flags.enabledDashboardMap) return 'mes'
  if (period === 'timeline' && !flags.enabledDashboardTimeline) return 'semana'
  return period
}

/**
 * Períodos com requisição própria (Calendário, Matriz, Mapa, Timeline) não usam a grade de
 * `GET /stations`.
 */
const OWN_FETCH_PERIODS: readonly LensPeriod[] = ['calendario', 'matriz', 'mapa', 'timeline']

/** `true` só para os períodos da grade de cards (Semana e Mês). */
export function periodUsesGrid(period: LensPeriod): boolean {
  return !OWN_FETCH_PERIODS.includes(period)
}

/**
 * Datas do recorte em instantes locais: `mes` = 1º dia 00:00 até o último 23:59:59.999
 * (sem os dias de fora do mês da grade); `semana` = a mesma semana da navegação.
 * O BE calcula os dias no fuso da operação (spec §3): sem `withInclusiveEndDate` aqui.
 */
export function matrixRange(cut: MatrixCut, anchor: Date): { startDate: Date; endDate: Date } {
  if (cut === 'semana') return getPeriodRange('semana', anchor)
  return { startDate: startOfMonth(anchor), endDate: endOfMonth(anchor) }
}

export function toApiMetric(metric: MatrixMetricUi): MatrixApiMetric {
  return metric === 'horas' ? 'hours' : 'stations'
}
